import { useState } from "preact/compat";
import { useEffect } from "preact/hooks";
import { api } from "../../../services";
import { checkoutBeneficiaryFormValidator } from "../../../utils";
import BeneficiaryNormalizer from "../../../utils/BeneficiaryNormalizer";
import { hasNumber } from "./beneficiaryForm.helper";

export const useBeneficiaryForm = props => {
    // Destructured props
    const {
        beneficiary,
        setOpen,
        rehydrateBeneficiariesState,
        handleBeneficiary,
        stockItem,
        handleCurrentBeneficiaryChange,
        source,
        nadOrder,
        productId = undefined
    } = props;

    // State to store the initial values
    const [initialValues] = useState(() => {
        if (Object.keys(beneficiary).length > 0) {
            // Formats the beneficiary data model in case they're not yet formatted
            return beneficiary.dob ?
                BeneficiaryNormalizer(beneficiary) :
                beneficiary;
        }

        return {
            isBrazilian: true,
            address: "",
            birthdate: "",
            cpf: "",
            city: "",
            email: "",
            gender: "",
            name: "",
            country: "",
            number: "",
            passport: "",
            phone: "",
            postalCode: "",
            affiliation: "",
            // Parental fields
            responsibleName: "",
            responsibleEmail: "",
            responsibleTaxVat: "",
            responsiblePassport: "",
            // Social name feature
            includeSocialName: false,
            socialName: "",
        }
    });
    // State to store all the actual form values
    const [formValues, setFormValues] = useState(initialValues);
    // State to store all the eventual form errors
    const [errors, setErrors] = useState({});
    // State to flag whether is edit mode or not
    const [isEditMode, setIsEditMode] = useState(false);
    // State to control the form submission load state
    const [isLoading, setIsLoading] = useState(false);
    // State to check whether the beneficiary is an adult or not
    const [isUnderEighteen, setIsUnderEighteen] = useState(() => {
        // if (initialValues.birthdate) {
        //     return calculateYearsDifference(initialValues.birthdate) < 18;
        // }

        return false;
    });
    // Stores any eventual errors the request may return
    const [submitError, setSubmitError] = useState("");

    // Whether the record under edit is the account holder's own beneficiary
    // (is_customer=1). The storefront cannot write it through /v1/beneficiary —
    // the backend rejects that with 403 (PEC-524) — so its update is routed
    // through the customer profile endpoint instead (PEC-623).
    // The flag reaches the frontend as a string ("0"/"1").
    const isTitular = Number(beneficiary?.isCustomer) === 1;

    // Form validator methods
    const {
        validateForm,
        validateAll
    } = checkoutBeneficiaryFormValidator({
        formValues,
        isUnderEighteen
    });

    // Method to handle all the inputs when their values get changed
    const handleChange = e => {
        // Destructured props
        let { name, value, type } = e.target;

        if (name === "name" && hasNumber(value)) {
            value = value
                .replace(/\d+/g, '');
        }

        // Formats the birthdate field with the right regex
        if (name === "birthdate") {
            value = value
                .replace(/\D/g, "")
                .replace(/^(\d{2})(\d)/g, "$1/$2")
                .replace(/(\d)(\d{4})$/, "$1/$2");

            // TODO: for some still unknown reason, this feature isn't working properly,
            //  but it's not on the frontend side. We're still deactivating it for now, though.
            // if (value.length === 10) {
            //     setIsUnderEighteen(calculateYearsDifference(value) < 18);
            // } else if (value.length < 10 && isUnderEighteen) {
            //     setIsUnderEighteen(false);
            // }
        }

        // Formats the CPF field with the right regex
        if (name === "cpf" || name === "responsibleTaxVat") {
            value = value
                .replace(/\D/g, '')
                .replace(/(\d{3})(\d)/, '$1.$2')
                .replace(/(\d{3})(\d)/, '$1.$2')
                .replace(/(\d{3})(\d)/, '$1-$2')
                .replace(/(-\d{2})\d+?$/, '$1');
        }

        // Sets the values to the formValues state
        setFormValues(prevState => {
            return {
                ...prevState,
                [name]: (
                    type !== "checkbox" || type === "radio"
                ) ? value : e.target.checked
            }
        })

        // Validates the value during input time
        setErrors(prevState => {
            return {
                ...prevState,
                ...validateForm({
                    target: name,
                    value
                })
            }
        })
    }

    // Method to handle the form submission
    const handleSubmit = e => {
        // Prevents page from reloading when form is submitted
        e.preventDefault();

        // Validates all fields at once
        const validationResults = validateAll();

        // The titular's CPF is read-only in the UI (PEC-504) and guarded by the
        // backend (PEC-524) — a legacy invalid CPF must not deadlock the submit
        // of the editable fields (same rule as the Meus Dados form).
        if (isTitular && formValues.isBrazilian) {
            validationResults.cpf = "";
        }

        // Appends the validated fields to the error state
        setErrors(validationResults);

        // Checks if there's any error in the form and, if so, abandons the method
        for (let i = 0; i < Object.values(validationResults).length; i++) {
            if (Object.values(validationResults)[i]) {
                return;
            }
        }

        // If validation is successful, then it does what it has to
        postData().then(() => { });
    }

    // Creates/updates a regular beneficiary through /v1/beneficiary/save.
    // Returns the saved item, or null when the backend refused the write
    // (the submit error is already set in that case).
    const saveBeneficiaryData = async () => {
        // Payload to create a beneficiary
        const data = {
            name: formValues.name || "",
            gender: parseInt(formValues.gender, 10),
            taxvat: formValues.cpf,
            dob: formValues.birthdate,
            email: formValues.email,
            nationality: formValues.isBrazilian ? 1 : 0,
            sponsor: "",
            affiliation: formValues.affiliation,
            includeSocialName: formValues.includeSocialName,
            socialName: formValues.socialName,
            // Foreigner fields
            ...(!formValues.isBrazilian && {
                passport: formValues.passport,
                country: formValues.country,
                phone: formValues.phone,
                postalCode: formValues.postalCode,
                city: formValues.city,
                address: formValues.address,
                number: formValues.number
            }),
            // Edit mode check
            ...(isEditMode && {
                update: true,
                entity_id: formValues.entityId
            }),
            // Edit mode check
            ...(isUnderEighteen && {
                responsibleName: formValues.responsibleName,
                responsibleEmail: formValues.responsibleEmail,
                responsibleTaxVat: formValues.responsibleTaxVat,
                responsiblePassport: formValues.responsiblePassport
            }),
            ...(productId && {
                productId
            })
        }

        // Tries to perform the request to the backend
        let response = await api.post("/v1/beneficiary/save", data);

        // Checks if there was any untracked error during the request
        if (response.data.error) {
            if (response.data?.code === 1110) {
                setSubmitError("Este CPF já está registrado para outro beneficiário.");
            } else {
                setSubmitError("Erro ao enviar o formulário.");
            }

            return null;
        }

        return response.data;
    }

    // Updates the titular through the customer profile endpoint (PEC-623).
    // The backend mirrors the change onto the titular row in the same
    // transaction (PEC-524), so the fresh list read below returns the updated
    // item in the raw shape the beneficiaries state stores.
    const updateTitularData = async () => {
        const data = {
            fullName: formValues.name || "",
            email: formValues.email,
            dob: formValues.birthdate,
            gender: parseInt(formValues.gender, 10),
            affiliation: formValues.affiliation,
            includeSocialName: formValues.includeSocialName,
            socialName: formValues.socialName,
            // The CPF field is read-only and goes back unchanged — the backend
            // rejects any actual change to it (403).
            ...(formValues.isBrazilian ?
                {taxvat: formValues.cpf} :
                {passport: formValues.passport})
        }

        const response = await api.put("/v1/customer/info", data);

        // The endpoint reports refusals in-body over HTTP 200
        // (e.g. {error: "Email já cadastrado", status: 400})
        if (response.data?.error) {
            setSubmitError(
                typeof response.data.error === "string" ?
                    response.data.error :
                    "Erro ao enviar o formulário."
            );

            return null;
        }

        const listResponse = await api.get("/v1/beneficiary");

        const freshItem = (listResponse.data?.items || []).find(item => {
            return parseInt(item.entityId, 10) === parseInt(beneficiary.entityId, 10);
        });

        if (!freshItem) {
            setSubmitError("Erro ao enviar o formulário.");

            return null;
        }

        return freshItem;
    }

    // Method to create or update a beneficiary
    const postData = async () => {
        setSubmitError("");

        try {
            setIsLoading(true);

            const savedBeneficiary = isTitular ?
                await updateTitularData() :
                await saveBeneficiaryData();

            // A handled refusal already set the submit error
            if (!savedBeneficiary) {
                return;
            }

            if (source !== "userLoggedInArea") {
                // Rehydrates the main beneficiaries state with the new one
                rehydrateBeneficiariesState({
                    data: savedBeneficiary,
                    action: isEditMode ?
                        "update" :
                        source !== "default" ?
                            "create" :
                            undefined
                });

                // This is responsible for automatically setting the beneficiary to the product
                // after it has been successfully created
                handleBeneficiary({
                    ...(source === "default" && {
                        stockItem,
                    }),
                    beneficiaryId: parseInt(savedBeneficiary.entityId, 10),
                    ...(source === "nad" && {
                        beneficiary: savedBeneficiary,
                        order: nadOrder
                    }),
                });

                // Updates the button label with the name of the created beneficiary
                handleCurrentBeneficiaryChange({
                    beneficiaryName: (
                        savedBeneficiary?.socialName || savedBeneficiary?.name
                    ) || ""
                });
            } else {
                // Rehydrates the main beneficiaries state with the new one in a more simple way
                rehydrateBeneficiariesState(savedBeneficiary);
            }

            setOpen(false);
        } catch (err) {
            console.error("Error while saving beneficiary:", err);

            const errorData = err?.response?.data;

            // Surfaces whatever the backend refused with — a bare {message}
            // body (e.g. the 403 titular guard) used to fail silently here.
            if (errorData?.code === 1110) {
                setSubmitError("Este CPF já está registrado para outro beneficiário.");
            } else if (typeof errorData?.message === "string" && errorData.message) {
                setSubmitError(errorData.message);
            } else {
                setSubmitError("Erro ao enviar o formulário.");
            }
        } finally {
            setIsLoading(false);
        }
    }

    // Performs the check for when it's in edit mode or not
    useEffect(() => {
        setIsEditMode(Object.keys(beneficiary).length > 0);
    }, [beneficiary]);

    useEffect(() => {
        // Validates all fields at once
        const validationResults = validateAll();

        // Appends the validated fields to the error state
        setErrors(validationResults);
    }, [formValues]);

    // FOR DEBUGGING PURPOSES ONLY
    // useEffect(() => {
    //     console.log("FORM VALUES", formValues);
    // }, [formValues]);

    return {
        formValues,
        errors,
        isLoading,
        isUnderEighteen,
        isTitular,
        submitError,
        handleChange,
        handleSubmit,
    }
}
